import React, { useEffect, useMemo, useRef, useState } from "react";
import { ClipboardCheck, CheckCircle2, Circle, Calendar, Eye, EyeOff } from "lucide-react";
import { SCHEDULE, TEAMS } from "../../data/tournament";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../lib/auth";

const findTeamId = (name) => TEAMS.find((t) => t.name === name)?.id || null;
const DAYS = ["Tous", "Vendredi 1 Mai", "Samedi 2 Mai", "Dimanche 3 Mai"];

const AttendancePanel = ({ initialGameNum = null, onConsumed }) => {
  const [filterDay, setFilterDay] = useState("Tous");
  const [gameNum, setGameNum] = useState(initialGameNum);
  const editorRef = useRef(null);

  // When the parent passes a pre-selected game (from Scores tab), open it and scroll into view.
  useEffect(() => {
    if (initialGameNum != null) {
      setGameNum(initialGameNum);
      // Adjust filter to make sure the game is visible in the picker (ex post)
      const g = SCHEDULE.find((s) => s.num === initialGameNum);
      if (g) setFilterDay("Tous");
      onConsumed?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialGameNum]);

  // Auto-scroll to the editor when a game is selected (especially helpful on mobile
  // where the editor sits below the picker list).
  useEffect(() => {
    if (gameNum != null && editorRef.current) {
      // Defer to next paint to ensure the editor has rendered.
      const t = setTimeout(() => {
        editorRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
      return () => clearTimeout(t);
    }
  }, [gameNum]);

  const games = useMemo(
    () => SCHEDULE.filter((g) => filterDay === "Tous" || g.day === filterDay),
    [filterDay]
  );

  const selectedGame = useMemo(() => SCHEDULE.find((g) => g.num === gameNum), [gameNum]);

  return (
    <div data-testid="admin-attendance-panel">
      <div className="mb-6">
        <span className="font-condensed uppercase tracking-[0.3em] text-xs text-[#E60012]">Alignements</span>
        <h2 className="mt-1 font-display text-4xl uppercase">Présences & statistiques</h2>
        <p className="mt-1 text-white/55 text-sm">
          Sélectionnez une partie, cochez les présents, puis saisissez buts / passes / pénalités
          en direct. Tout est sauvegardé automatiquement.
        </p>
      </div>

      <StatsVisibilityToggle />

      <div className="mb-4 flex items-center gap-2 flex-wrap">
        {DAYS.map((d) => (
          <button
            key={d}
            onClick={() => setFilterDay(d)}
            data-testid={`attendance-day-${d.split(" ")[0].toLowerCase()}`}
            className={`px-3.5 py-2 text-xs font-condensed uppercase tracking-[0.15em] transition-colors ${
              filterDay === d
                ? "bg-[#E60012] text-white"
                : "bg-transparent border border-white/15 text-white/70 hover:border-[#E60012]"
            }`}
          >
            {d}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-[320px_1fr] gap-6">
        {/* Game picker */}
        <div className="bg-[#111] border border-white/10 max-h-[40vh] lg:max-h-[70vh] overflow-y-auto">
          <div className="sticky top-0 bg-[#111] px-4 py-3 border-b border-white/10 flex items-center gap-2 text-white/60 text-xs font-condensed uppercase tracking-[0.2em]">
            <Calendar className="w-3.5 h-3.5 text-[#E60012]" /> {games.length} partie{games.length > 1 ? "s" : ""}
          </div>
          {games.map((g) => (
            <button
              key={g.num}
              onClick={() => setGameNum(g.num)}
              data-testid={`attendance-game-${g.num}`}
              className={`w-full text-left px-4 py-3 border-t border-white/5 hover:bg-[#E60012]/10 transition-colors ${
                gameNum === g.num ? "bg-[#E60012]/15 border-l-2 border-l-[#E60012]" : ""
              }`}
            >
              <div className="flex items-center gap-2 text-xs font-condensed uppercase tracking-[0.15em] text-white/60">
                <span className="font-display text-lg text-[#E60012]">#{g.num}</span>
                <span>{g.time}</span>
                <span className="text-white/30">·</span>
                <span>{g.category}</span>
              </div>
              <div className="mt-1 text-sm font-condensed text-white truncate">
                {g.away} <span className="text-white/40">vs</span> {g.home}
              </div>
            </button>
          ))}
        </div>

        {/* Editor */}
        <div ref={editorRef}>
          {!selectedGame ? (
            <div className="bg-[#111] border border-white/10 p-8 text-center text-white/40 font-condensed uppercase tracking-[0.2em] text-xs">
              Sélectionnez une partie à gauche pour gérer les présences.
            </div>
          ) : (
            <AttendanceEditor key={selectedGame.num} game={selectedGame} />
          )}
        </div>
      </div>
    </div>
  );
};

const AttendanceEditor = ({ game }) => {
  const awayId = findTeamId(game.away);
  const homeId = findTeamId(game.home);

  return (
    <div className="space-y-4">
      <div className="bg-[#111] border border-white/10 px-5 py-4">
        <div className="flex items-center gap-3 text-xs font-condensed uppercase tracking-[0.2em] text-white/55">
          <span className="font-display text-2xl text-[#E60012] leading-none">#{game.num}</span>
          <span>{game.day}</span>
          <span>·</span>
          <span>{game.time}</span>
          <span>·</span>
          <span>{game.ice}</span>
          {game.phase && (
            <span className="ml-auto px-2 py-0.5 bg-[#E60012]/20 text-[#E60012] border border-[#E60012]/40 text-[10px]">
              {game.phase}
            </span>
          )}
        </div>
      </div>

      {!awayId || !homeId ? (
        <div className="bg-[#111] border border-white/10 p-6 text-center text-white/40 font-condensed uppercase tracking-[0.2em] text-xs">
          Les équipes de cette partie seront déterminées à la fin de la phase préliminaire.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          <TeamAttendanceList gameNum={game.num} teamId={awayId} teamName={game.away} label="Visiteur" />
          <TeamAttendanceList gameNum={game.num} teamId={homeId} teamName={game.home} label="Local" />
        </div>
      )}
    </div>
  );
};

const TeamAttendanceList = ({ gameNum, teamId, teamName, label }) => {
  const [roster, setRoster] = useState([]);
  const [attendance, setAttendance] = useState({}); // {player_id: true/false}
  const [stats, setStats] = useState({}); // {player_id: {goals, assists, pim}}
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null); // player_id in progress
  const [error, setError] = useState("");

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      const [r, a, st] = await Promise.all([
        api.get(`/teams/${teamId}/roster`).then((x) => x.data).catch(() => []),
        api.get(`/games/${gameNum}/attendance`).then((x) => x.data).catch(() => []),
        api.get(`/games/${gameNum}/stats`).then((x) => x.data).catch(() => []),
      ]);
      if (cancel) return;
      setRoster(r);
      const pmap = {};
      a.filter((x) => x.team_id === teamId).forEach((x) => (pmap[x.player_id] = x.present));
      setAttendance(pmap);
      const smap = {};
      st.filter((x) => x.team_id === teamId).forEach((x) => (smap[x.player_id] = { goals: x.goals, assists: x.assists, pim: x.pim }));
      setStats(smap);
      setLoading(false);
    })();
    return () => {
      cancel = true;
    };
  }, [gameNum, teamId]);

  const toggle = async (playerId) => {
    const current = attendance[playerId] !== false; // default present
    const next = !current;
    setAttendance((m) => ({ ...m, [playerId]: next }));
    setSaving(playerId);
    setError("");
    try {
      await api.put(`/admin/games/${gameNum}/attendance/${teamId}/${playerId}`, { present: next });
    } catch (e) {
      setAttendance((m) => ({ ...m, [playerId]: current })); // revert
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(null);
    }
  };

  const setAll = async (value) => {
    setError("");
    for (const p of roster) {
      setAttendance((m) => ({ ...m, [p.id]: value }));
      try {
        await api.put(`/admin/games/${gameNum}/attendance/${teamId}/${p.id}`, { present: value });
      } catch (e) {
        setError(formatApiError(e.response?.data?.detail) || e.message);
      }
    }
  };

  const bumpStat = async (playerId, field, delta) => {
    const cur = stats[playerId] || { goals: 0, assists: 0, pim: 0 };
    const next = { ...cur, [field]: Math.max(0, (cur[field] || 0) + delta) };
    setStats((m) => ({ ...m, [playerId]: next }));
    try {
      await api.put(`/admin/games/${gameNum}/stats/${teamId}/${playerId}`, next);
    } catch (e) {
      setStats((m) => ({ ...m, [playerId]: cur }));
      setError(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const presentCount = roster.filter((p) => attendance[p.id] !== false).length;

  return (
    <div className="bg-[#111] border border-white/10 overflow-hidden" data-testid={`attendance-team-${teamId}`}>
      <div className="bg-black/40 px-4 py-3 border-b border-white/10">
        <div className="flex items-center gap-2 text-[10px] font-condensed uppercase tracking-[0.3em] text-[#E60012]">
          <ClipboardCheck className="w-3 h-3" /> {label}
        </div>
        <div className="mt-0.5 flex items-center justify-between">
          <div className="font-display text-xl uppercase truncate">{teamName}</div>
          <div className="text-xs font-condensed uppercase tracking-[0.2em] text-white/60 shrink-0 ml-2">
            {presentCount}/{roster.length}
          </div>
        </div>
        {roster.length > 0 && (
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              onClick={() => setAll(true)}
              data-testid={`attendance-all-present-${teamId}`}
              className="px-2.5 py-1 border border-white/15 hover:border-emerald-500 hover:text-emerald-400 text-[10px] font-condensed uppercase tracking-[0.2em]"
            >
              Tous présents
            </button>
            <button
              type="button"
              onClick={() => setAll(false)}
              data-testid={`attendance-all-absent-${teamId}`}
              className="px-2.5 py-1 border border-white/15 hover:border-red-500 hover:text-red-400 text-[10px] font-condensed uppercase tracking-[0.2em]"
            >
              Tous absents
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="p-5 text-center text-white/40 font-condensed uppercase tracking-[0.2em] text-xs">
          Chargement…
        </div>
      ) : roster.length === 0 ? (
        <div className="p-5 text-center text-white/40 font-condensed uppercase tracking-[0.2em] text-xs">
          Aucun joueur enregistré pour cette équipe.
        </div>
      ) : (
        <ul className="max-h-[60vh] overflow-y-auto">
          {roster.map((p, idx) => {
            const present = attendance[p.id] !== false;
            const isSaving = saving === p.id;
            const s = stats[p.id] || { goals: 0, assists: 0, pim: 0 };
            return (
              <li
                key={p.id}
                className={`px-4 py-2.5 border-t border-white/5 ${idx % 2 === 0 ? "bg-[#0c0c0c]" : "bg-[#141414]"}`}
              >
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => toggle(p.id)}
                    disabled={isSaving}
                    data-testid={`attendance-toggle-${p.id}`}
                    className="shrink-0"
                  >
                    {present ? (
                      <CheckCircle2 className="w-5 h-5 text-emerald-400 hover:text-emerald-300" />
                    ) : (
                      <Circle className="w-5 h-5 text-white/25 hover:text-white/60" />
                    )}
                  </button>
                  <div className="font-display text-lg text-[#E60012] w-8 shrink-0">{p.number || "—"}</div>
                  <div className="flex-1 min-w-0">
                    <div className={`font-condensed truncate ${present ? "text-white" : "text-white/35 line-through"}`}>
                      {p.name}
                    </div>
                    {p.position && (
                      <div className="text-[10px] text-white/40 uppercase tracking-[0.2em] font-condensed">
                        {p.position}
                      </div>
                    )}
                  </div>
                </div>
                {present && (
                  <div className="mt-2 ml-8 grid grid-cols-3 gap-1.5">
                    <StatStepper label="B" value={s.goals} onInc={() => bumpStat(p.id, "goals", 1)} onDec={() => bumpStat(p.id, "goals", -1)} testid={`stat-goals-${p.id}`} />
                    <StatStepper label="A" value={s.assists} onInc={() => bumpStat(p.id, "assists", 1)} onDec={() => bumpStat(p.id, "assists", -1)} testid={`stat-assists-${p.id}`} />
                    <StatStepper label="PIM" value={s.pim} onInc={() => bumpStat(p.id, "pim", 2)} onDec={() => bumpStat(p.id, "pim", -2)} testid={`stat-pim-${p.id}`} />
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {error && (
        <div className="px-4 py-2 bg-red-500/10 border-t border-red-500/30 text-red-400 text-xs font-condensed">
          {error}
        </div>
      )}
    </div>
  );
};

const StatStepper = ({ label, value, onInc, onDec, testid }) => (
  <div className="flex items-center bg-black/40 border border-white/10 overflow-hidden" data-testid={testid}>
    <button type="button" onClick={onDec} className="px-1.5 py-1 text-white/60 hover:text-white hover:bg-white/5 text-xs leading-none">−</button>
    <div className="flex-1 text-center py-0.5 leading-tight">
      <div className="text-[8px] text-white/40 font-condensed uppercase tracking-wider">{label}</div>
      <div className="font-display text-sm text-white leading-none">{value}</div>
    </div>
    <button type="button" onClick={onInc} className="px-1.5 py-1 text-white/60 hover:text-[#E60012] hover:bg-white/5 text-xs leading-none">+</button>
  </div>
);

const StatsVisibilityToggle = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [enabled, setEnabled] = useState(null); // null = loading
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/settings").then((r) => setEnabled(!!r.data.stats_public)).catch(() => setEnabled(false));
  }, []);

  const toggle = async () => {
    if (!isAdmin || enabled === null || saving) return;
    const next = !enabled;
    setSaving(true);
    setError("");
    try {
      const r = await api.put("/admin/settings", { stats_public: next });
      setEnabled(!!r.data.stats_public);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mb-6 bg-[#111] border border-white/10 p-5 flex items-start gap-4 flex-wrap" data-testid="stats-visibility-toggle">
      <div className="flex-1 min-w-[220px]">
        <div className="flex items-center gap-2">
          {enabled ? <Eye className="w-4 h-4 text-emerald-400" /> : <EyeOff className="w-4 h-4 text-white/55" />}
          <span className="font-condensed uppercase tracking-[0.2em] text-xs text-white/60">
            Visibilité publique des statistiques
          </span>
        </div>
        <p className="mt-1 text-sm text-white/75">
          {enabled === null
            ? "Chargement…"
            : enabled
            ? "Les statistiques (buts / passes / PIM et top 10 pointeurs) sont ACTUELLEMENT visibles au public."
            : "Les statistiques sont MASQUÉES au public. Les alignements (présences) restent toujours visibles."}
        </p>
        {!isAdmin && (
          <p className="mt-2 text-[11px] text-white/40 font-condensed uppercase tracking-[0.15em]">
            Seul un organisateur peut modifier ce paramètre.
          </p>
        )}
        {error && <p className="mt-2 text-xs text-red-400 font-condensed">{error}</p>}
      </div>
      <button
        type="button"
        onClick={toggle}
        disabled={!isAdmin || enabled === null || saving}
        data-testid="stats-visibility-switch"
        className={`relative inline-flex h-9 w-20 items-center shrink-0 transition-colors border ${
          enabled
            ? "bg-emerald-500/20 border-emerald-500/60"
            : "bg-black/40 border-white/15"
        } ${!isAdmin || saving ? "opacity-60 cursor-not-allowed" : ""}`}
      >
        <span
          className={`inline-block h-6 w-6 transform transition-transform ${
            enabled ? "translate-x-[52px] bg-emerald-400" : "translate-x-1 bg-white/40"
          }`}
        />
        <span className={`absolute text-[10px] font-condensed uppercase tracking-[0.2em] ${enabled ? "left-2 text-emerald-300" : "right-2 text-white/50"}`}>
          {enabled ? "ON" : "OFF"}
        </span>
      </button>
    </div>
  );
};

export default AttendancePanel;